
import React, { useState, useEffect } from 'react';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { gameCounterService } from '@/services/gamification/counters/gameCounterService';
import { AlertCircle, RefreshCw, Save } from 'lucide-react';
import { useAppSettings } from '@/hooks/useAppSettings';

const GameCounterReset: React.FC = () => {
  const { t } = useTranslationUtils();
  const { toast } = useToast();
  const { gamesAllowedPerDay, refetchSettings } = useAppSettings();
  
  const [userId, setUserId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [resetAllLoading, setResetAllLoading] = useState(false);
  const [gamesAllowed, setGamesAllowed] = useState<number>(gamesAllowedPerDay || 3);
  const [savingGamesAllowed, setSavingGamesAllowed] = useState(false);
  
  // Update local state when app settings are loaded
  useEffect(() => {
    if (gamesAllowedPerDay) {
      setGamesAllowed(gamesAllowedPerDay);
    }
  }, [gamesAllowedPerDay]);
  
  const handleResetSingleUser = async () => {
    if (!userId || userId.trim() === '') {
      toast({
        title: t('admin:error', 'Error'),
        description: t('admin:userIdRequired', 'User ID is required'),
        variant: 'destructive',
      });
      return;
    }
    
    setLoading(true);
    try {
      const success = await gameCounterService.resetGameCounterForUser(userId);
      
      if (success) {
        toast({
          title: t('admin:success', 'Success'),
          description: t('admin:gameCounterReset', 'Game counter has been reset for this user'),
        });
      } else {
        toast({
          title: t('admin:error', 'Error'),
          description: t('admin:gameCounterResetFailed', 'Failed to reset game counter'),
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error resetting game counter:', error);
      toast({
        title: t('admin:error', 'Error'),
        description: t('admin:gameCounterResetFailed', 'Failed to reset game counter'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleResetAllUsers = async () => {
    setResetAllLoading(true);
    try {
      const success = await gameCounterService.resetAllGameCounters();
      
      if (success) {
        toast({
          title: t('admin:success', 'Success'),
          description: t('admin:allGameCountersReset', 'All game counters have been reset'),
        });
      } else {
        toast({
          title: t('admin:error', 'Error'),
          description: t('admin:allGameCountersResetFailed', 'Failed to reset all game counters'),
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error resetting all game counters:', error);
      toast({
        title: t('admin:error', 'Error'),
        description: t('admin:allGameCountersResetFailed', 'Failed to reset all game counters'),
        variant: 'destructive',
      });
    } finally {
      setResetAllLoading(false);
    }
  };
  
  const handleSaveGamesAllowed = async () => {
    setSavingGamesAllowed(true);
    try {
      const success = await gameCounterService.setGamesAllowedPerDay(gamesAllowed);
      
      if (success) {
        toast({
          title: t('admin:success', 'Success'),
          description: t('admin:gamesAllowedUpdated', 'Games allowed per day has been updated'),
        });
        
        // Refresh app settings
        await refetchSettings();
      } else {
        toast({
          title: t('admin:error', 'Error'),
          description: t('admin:gamesAllowedUpdateFailed', 'Failed to update games allowed per day'),
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error updating games allowed per day:', error);
      toast({
        title: t('admin:error', 'Error'),
        description: t('admin:gamesAllowedUpdateFailed', 'Failed to update games allowed per day'),
        variant: 'destructive',
      });
    } finally {
      setSavingGamesAllowed(false);
    }
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin:gameCounterTools', 'Game Counter Tools')}</CardTitle>
        <CardDescription>
          {t('admin:gameCounterToolsDesc', 'Reset game counters for users to allow them to play more games today')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Games Allowed Per Day Section */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium">{t('admin:gamesAllowedPerDay', 'Games Allowed Per Day')}</h3>
          <div className="flex gap-2">
            <Input 
              type="number"
              min="1"
              max="20"
              value={gamesAllowed}
              onChange={(e) => setGamesAllowed(Number(e.target.value))}
            />
            <Button 
              onClick={handleSaveGamesAllowed} 
              disabled={savingGamesAllowed || gamesAllowed === gamesAllowedPerDay}
              size="sm"
            >
              {savingGamesAllowed ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {t('admin:save', 'Save')}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('admin:gamesAllowedDesc', 'Set how many games users can play per day')}
          </p>
        </div>
        
        {/* Reset Single User Section */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium">{t('admin:resetSingleUser', 'Reset Single User')}</h3>
          <div className="flex gap-2">
            <Input 
              placeholder={t('admin:enterUserId', 'Enter user ID')} 
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            />
            <Button 
              onClick={handleResetSingleUser} 
              disabled={loading || !userId}
              size="sm"
            >
              {loading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {t('admin:reset', 'Reset')}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('admin:resetUserDesc', 'This will set the games_played_today counter to 0 for this user')}
          </p>
        </div>
        
        {/* Reset All Users Section */}
        <div className="pt-2">
          <div className="rounded-md bg-amber-50 p-4 border border-amber-200">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-amber-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-800">
                  {t('admin:adminOnly', 'Admin Only Feature')}
                </h3>
                <div className="mt-2 text-sm text-amber-700">
                  <p>
                    {t('admin:resetAllWarning', 'Resetting all game counters will allow all users to play more games today. Use with caution.')}
                  </p>
                </div>
                <div className="mt-4">
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleResetAllUsers}
                    disabled={resetAllLoading}
                  >
                    {resetAllLoading ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    {t('admin:resetAllCounters', 'Reset All Game Counters')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default GameCounterReset;
